import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity,
  BookOpen,
  Brain,
  ChevronLeft,
  ClipboardList,
  Flame,
  LayoutDashboard,
  LogOut,
  KeyRound,
  ScrollText,
  Search,
  ShieldCheck,
  Stethoscope,
  Trophy,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";

import { useIntl } from "react-intl";

import { Button } from "@/components/ui/button";
import { LocaleSwitcher } from "@/components/locale-switcher";
import { useAuthStore } from "@/lib/auth-store";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  group: "clinico" | "aprendizaje" | "docente" | "cuenta";
  teacherOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: "/app/cases", labelKey: "nav.library", icon: Stethoscope, group: "clinico" },
  { to: "/app/active", labelKey: "nav.active", icon: Activity, group: "clinico" },
  { to: "/app/cognitive", labelKey: "nav.cognitive", icon: Brain, group: "aprendizaje" },
  { to: "/app/srs", labelKey: "nav.srs", icon: Flame, group: "aprendizaje" },
  { to: "/app/mcq", labelKey: "nav.mcq", icon: ClipboardList, group: "aprendizaje" },
  { to: "/app/evidence", labelKey: "nav.evidence", icon: Search, group: "aprendizaje" },
  { to: "/app/anatomy", labelKey: "nav.anatomy", icon: ShieldCheck, group: "aprendizaje" },
  { to: "/app/viva", labelKey: "nav.viva", icon: ScrollText, group: "aprendizaje" },
  { to: "/app/achievements", labelKey: "nav.achievements", icon: Trophy, group: "aprendizaje" },
  { to: "/app/teacher", labelKey: "nav.teacher.dashboard", icon: LayoutDashboard, group: "docente", teacherOnly: true },
  { to: "/app/teacher/case-builder", labelKey: "nav.teacher.caseBuilder", icon: BookOpen, group: "docente", teacherOnly: true },
  { to: "/app/settings/security", labelKey: "nav.security", icon: KeyRound, group: "cuenta" },
];

const GROUP_LABEL_KEYS: Record<NavItem["group"], string> = {
  clinico: "nav.group.clinical",
  aprendizaje: "nav.group.learning",
  docente: "nav.group.teacher",
  cuenta: "nav.group.account",
};

export function AppLayout() {
  const intl = useIntl();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const logout = useAuthStore((s) => s.logout);
  const [collapsed, setCollapsed] = useState(false);

  if (!accessToken) return <Navigate to="/login" replace />;

  const isTeacher =
    user?.role === "teacher" ||
    user?.role === "institution_admin" ||
    user?.role === "platform_admin";

  const visibleNav = NAV.filter((n) => (n.teacherOnly ? isTeacher : true));

  const groups: Array<{ group: NavItem["group"]; items: NavItem[] }> = [
    { group: "clinico", items: visibleNav.filter((n) => n.group === "clinico") },
    { group: "aprendizaje", items: visibleNav.filter((n) => n.group === "aprendizaje") },
    { group: "docente", items: visibleNav.filter((n) => n.group === "docente") },
    { group: "cuenta", items: visibleNav.filter((n) => n.group === "cuenta") },
  ];

  return (
    <div className="flex min-h-screen bg-[var(--color-base)]">
      <a
        href="#main-content"
        className="sr-only absolute left-4 top-4 z-[100] -translate-y-16 rounded-md bg-[var(--color-blue)] px-4 py-2 text-sm font-medium text-white shadow-lg transition-transform focus:not-sr-only focus:translate-y-0"
      >
        Saltar al contenido principal
      </a>
      <aside
        className={cn(
          "fixed left-0 top-0 z-20 flex h-screen flex-col border-r border-[var(--color-border)] bg-[var(--color-navy-dark)] transition-all duration-300",
          collapsed ? "w-[68px]" : "w-[240px]",
        )}
      >
        <div className="flex h-16 items-center gap-3 border-b border-white/5 px-5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-blue)]/20 text-[var(--color-blue-soft)]">
            <Stethoscope size={16} />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <div className="font-[var(--font-display)] text-sm font-bold text-white">
                SimuMed
              </div>
              <div className="font-[var(--font-mono)] text-[9px] uppercase tracking-widest text-white/30">
                Hospital virtual · dev
              </div>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {groups.map(
            ({ group, items }) =>
              items.length > 0 && (
                <div key={group} className="mb-4">
                  {!collapsed && (
                    <div className="mb-1.5 px-3 font-[var(--font-mono)] text-[9px] font-semibold uppercase tracking-widest text-white/30">
                      {intl.formatMessage({ id: GROUP_LABEL_KEYS[group] })}
                    </div>
                  )}
                  <ul className="space-y-0.5">
                    {items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.to === "/app/cases"}
                          className={({ isActive }) =>
                            cn(
                              "group relative flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-[13px] transition-colors",
                              isActive
                                ? "bg-[var(--color-blue)]/20 text-white"
                                : "text-white/65 hover:bg-white/5 hover:text-white",
                            )
                          }
                        >
                          {({ isActive }) => (
                            <>
                              {isActive && (
                                <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-[var(--color-blue-soft)]" />
                              )}
                              <item.icon size={15} className="shrink-0" />
                              {!collapsed && (
                                <span className="truncate">
                                  {intl.formatMessage({ id: item.labelKey })}
                                </span>
                              )}
                            </>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
          )}
        </nav>

        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex h-11 items-center gap-2 border-t border-white/5 px-5 text-[11px] font-medium uppercase tracking-widest text-white/40 hover:bg-white/5 hover:text-white"
        >
          <ChevronLeft
            size={14}
            className={cn("transition-transform", collapsed && "rotate-180")}
          />
          {!collapsed && <span>{intl.formatMessage({ id: "nav.collapse", defaultMessage: "Colapsar" })}</span>}
        </button>
      </aside>

      <div
        className={cn(
          "flex min-h-screen flex-1 flex-col transition-all duration-300",
          collapsed ? "ml-[68px]" : "ml-[240px]",
        )}
      >
        <header className="sticky top-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-base)]/85 backdrop-blur-md">
          <div className="flex h-16 items-center gap-4 px-6 lg:px-8">
            <div className="ml-auto flex items-center gap-3">
              <LocaleSwitcher />
              {user ? (
                <div className="hidden items-center gap-3 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] py-1 pr-4 pl-1 sm:flex">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-navy)] text-[11px] font-bold text-white">
                    {user.full_name
                      .split(" ")
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join("")}
                  </div>
                  <div className="text-[12px] leading-tight">
                    <div className="font-semibold text-[var(--color-text-primary)]">
                      {user.full_name}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
                      {user.role.replace("_", " ")}
                    </div>
                  </div>
                </div>
              ) : null}

              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                aria-label={intl.formatMessage({ id: "nav.logout" })}
              >
                <LogOut size={16} />
              </Button>
            </div>
          </div>
        </header>

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-7xl flex-1 p-6 lg:p-8 focus:outline-none"
        >
          <Outlet />
        </main>

        <footer className="mx-auto flex h-10 w-full max-w-7xl items-center justify-center gap-2 border-t border-[var(--color-border)] text-[11px] text-[var(--color-text-muted)]">
          <Zap size={12} className="text-[var(--color-gold)]" />
          <span>SimuMed · 2026</span>
        </footer>
      </div>
    </div>
  );
}
